import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ArticleFacets,
  ArticleFlagFilter,
  ArticleResponse,
  ArticleStockStatus,
  ArticleType,
  ArticleUnit,
  AuditLogEntry,
  CreateArticleInput,
  CreateArticleVariantInput,
  ItemResponse,
  ListResponse,
  PriceHistoryEntry,
  UpdateArticleInput,
  UpdateArticleVariantInput,
} from '@proerp/shared';
import { apiRequest, collectAllPages } from '@/lib/apiClient';

const ARTICLES_KEY = ['articles'] as const;

export interface ArticlesQuery {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  sourcing?: 'MAKE' | 'BUY';
  belowMinStock?: boolean;
  active?: boolean;
  /** Sub-Artikel (Ablängungen) mitlisten — z. B. für die Auswahl im Belegeditor (ADR 0040). */
  includeVariants?: boolean;

  // Erweiterte Filter des Artikelstamms (2026-08-11).
  articleType?: ArticleType;
  unit?: ArticleUnit;
  stockStatus?: ArticleStockStatus;
  /** Mehrfachauswahl — wird kommagetrennt übertragen. */
  categories?: string[];
  suppliers?: string[];
  manufacturer?: string;
  countryOfOrigin?: string;
  commodityMainGroup?: number;
  isBlocked?: ArticleFlagFilter;
  isBillOfMaterials?: ArticleFlagFilter;
  isCuttable?: ArticleFlagFilter;
  /** VK-Preisgrenzen in Euro (der Server rechnet in Mikro-Einheiten um). */
  salesPriceMin?: number;
  salesPriceMax?: number;
  stockMin?: number;
  stockMax?: number;
}

function buildQueryString(query: ArticlesQuery): string {
  const params = new URLSearchParams();
  params.set('page', String(query.page ?? 1));
  params.set('pageSize', String(query.pageSize ?? 50));
  if (query.sort) params.set('sort', query.sort);
  if (query.q) params.set('q', query.q);
  if (query.sourcing) params.set('sourcing', query.sourcing);
  if (query.belowMinStock) params.set('belowMinStock', 'true');
  if (query.active !== undefined) params.set('active', String(query.active));
  if (query.includeVariants) params.set('includeVariants', 'true');
  if (query.articleType) params.set('articleType', query.articleType);
  if (query.unit) params.set('unit', query.unit);
  if (query.stockStatus) params.set('stockStatus', query.stockStatus);
  if (query.categories?.length) params.set('categories', query.categories.join(','));
  if (query.suppliers?.length) params.set('suppliers', query.suppliers.join(','));
  if (query.manufacturer) params.set('manufacturer', query.manufacturer);
  if (query.countryOfOrigin) params.set('countryOfOrigin', query.countryOfOrigin);
  if (query.commodityMainGroup !== undefined) params.set('commodityMainGroup', String(query.commodityMainGroup));
  if (query.isBlocked) params.set('isBlocked', query.isBlocked);
  if (query.isBillOfMaterials) params.set('isBillOfMaterials', query.isBillOfMaterials);
  if (query.isCuttable) params.set('isCuttable', query.isCuttable);
  if (query.salesPriceMin !== undefined) params.set('salesPriceMin', String(query.salesPriceMin));
  if (query.salesPriceMax !== undefined) params.set('salesPriceMax', String(query.salesPriceMax));
  if (query.stockMin !== undefined) params.set('stockMin', String(query.stockMin));
  if (query.stockMax !== undefined) params.set('stockMax', String(query.stockMax));
  return params.toString();
}

/** Nicht-Hook-Fetch einer Artikel-Seite (für „Export alles" via collectAllPages). */
export function fetchArticlesPage(query: ArticlesQuery): Promise<ListResponse<ArticleResponse>> {
  return apiRequest<ListResponse<ArticleResponse>>(`/v1/articles?${buildQueryString(query)}`);
}

export function useArticles(query: ArticlesQuery) {
  return useQuery({
    queryKey: [...ARTICLES_KEY, query],
    queryFn: () => apiRequest<ListResponse<ArticleResponse>>(`/v1/articles?${buildQueryString(query)}`),
    placeholderData: keepPreviousData,
  });
}

/**
 * Lädt ALLE Artikel (für Auswertungen mit korrekten Summen/Verteilungen und für
 * Artikel-Auswahllisten). `enabled: false` hält den Abruf zurück, bis er
 * gebraucht wird — der Stamm hat mehrere Tausend Zeilen und soll nicht beim
 * bloßen Öffnen einer Seite mitgeladen werden, die ihn nur im Dialog braucht.
 */
export function useAllArticles(options?: { includeVariants?: boolean; enabled?: boolean }) {
  const includeVariants = options?.includeVariants ?? false;
  return useQuery({
    queryKey: [...ARTICLES_KEY, 'all', { includeVariants }],
    queryFn: () =>
      collectAllPages((page, pageSize) => fetchArticlesPage({ page, pageSize, includeVariants })),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Belegte Filterwerte (Kategorie/Lieferant/Hersteller/Land) für die
 * Auswahllisten der Filterleiste. Lange Standzeit: der Artikelstamm bekommt
 * nicht im Minutentakt neue Kategorien, und die Liste hängt an jeder Ansicht.
 */
export function useArticleFacets() {
  return useQuery({
    queryKey: [...ARTICLES_KEY, 'facets'],
    queryFn: () => apiRequest<ItemResponse<ArticleFacets>>('/v1/articles/facets'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useArticle(id: string) {
  return useQuery({
    queryKey: [...ARTICLES_KEY, 'detail', id],
    queryFn: () => apiRequest<ItemResponse<ArticleResponse>>(`/v1/articles/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateArticleInput) =>
      apiRequest<ItemResponse<ArticleResponse>>('/v1/articles', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ARTICLES_KEY }),
  });
}

export function useUpdateArticle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateArticleInput) =>
      apiRequest<ItemResponse<ArticleResponse>>(`/v1/articles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ARTICLES_KEY }),
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/articles/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ARTICLES_KEY }),
  });
}

export function useArchiveArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<ArticleResponse>>(`/v1/articles/${id}/archive`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ARTICLES_KEY }),
  });
}

/** Abläng-Varianten eines Basisartikels (ADR 0040), nach Länge sortiert. */
export function useArticleVariants(articleId: string) {
  return useQuery({
    queryKey: [...ARTICLES_KEY, 'variants', articleId],
    queryFn: () => apiRequest<ListResponse<ArticleResponse>>(`/v1/articles/${articleId}/variants`),
    enabled: Boolean(articleId),
  });
}

/** Variante materialisieren (mit Dedup) — legt bei Bedarf einen eigenen Stück-Artikel an. */
export function useCreateArticleVariant(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateArticleVariantInput) =>
      apiRequest<ItemResponse<ArticleResponse>>(`/v1/articles/${articleId}/variants`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ARTICLES_KEY, 'variants', articleId] });
      queryClient.invalidateQueries({ queryKey: ARTICLES_KEY });
    },
  });
}

/**
 * Sub-Artikel bearbeiten (ADR 0040): nur Länge/Gewicht/Lagerstand — Rest erbt vom
 * Mutterartikel. `variantId` ist die ID des Sub-Artikels selbst.
 */
export function useUpdateArticleVariant(variantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateArticleVariantInput) =>
      apiRequest<ItemResponse<ArticleResponse>>(`/v1/articles/${variantId}/variant`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ARTICLES_KEY }),
  });
}

/** EK/VK-Preis-Historie eines Artikels (älteste zuerst). */
/**
 * Änderungsverlauf eines Artikels (Reiter „Änderungen"). Serverseitig auf
 * `inventory.read` gestellt — wer den Artikel sehen darf, darf auch sehen, wer
 * ihn geändert hat. Der globale Audit-Log bleibt Administratoren vorbehalten.
 */
export function useArticleHistory(articleId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: ['articles', articleId, 'history', page, pageSize],
    queryFn: () =>
      apiRequest<ListResponse<AuditLogEntry>>(
        `/v1/articles/${articleId}/history?page=${page}&pageSize=${pageSize}`,
      ),
    enabled: Boolean(articleId),
  });
}

export function usePriceHistory(articleId: string) {
  return useQuery({
    queryKey: ['articles', articleId, 'price-history'],
    queryFn: () =>
      apiRequest<ListResponse<PriceHistoryEntry>>(`/v1/articles/${articleId}/price-history`),
    enabled: Boolean(articleId),
  });
}
